import type { DocumentSortKey } from "@/core/lib/documents-api";
import type { DocumentsPageFilters } from "@/hooks/use-document-store";

/**
 * The documents library's view state, as held in the URL.
 *
 * Parsing lives here rather than in the component because the route loader
 * reads the same query string: it starts the first page's fetch as the
 * navigation begins, and must arrive at exactly the filters — and so the same
 * query key — the component will subscribe to a moment later.
 */

const SORT_KEYS: readonly DocumentSortKey[] = ["updated", "name", "type", "status"];

/** Newest first — what the library shows with no `sort` in the URL. */
export const DEFAULT_SORT = { key: "updated", dir: "desc" } as const satisfies {
  key: DocumentSortKey;
  dir: "asc" | "desc";
};

export type StatusFilter = "all" | "draft" | "final" | "signed";

export const PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_SIZE = 25;

export type DocumentsView = {
  q: string;
  type: string;
  status: StatusFilter;
  sort: { key: DocumentSortKey; dir: "asc" | "desc" };
  pageSize: number;
  /** The same values in the shape the paginated query takes. */
  filters: DocumentsPageFilters;
};

/** Reads the library's committed view out of a query string. */
export function parseDocumentsSearch(params: URLSearchParams): DocumentsView {
  const q = params.get("q")?.trim() ?? "";
  const type = params.get("type") ?? "all";
  const status = (params.get("status") as StatusFilter | null) ?? "all";
  const sort = parseSort(params);
  const sizeParam = Number(params.get("size"));
  const pageSize = PAGE_SIZES.includes(sizeParam as (typeof PAGE_SIZES)[number])
    ? sizeParam
    : DEFAULT_SIZE;

  return {
    q,
    type,
    status,
    sort,
    pageSize,
    filters: {
      pageSize,
      q: q || undefined,
      type: type === "all" ? undefined : type,
      status,
      sort: sort.key,
      dir: sort.dir,
    },
  };
}

/**
 * `?sort=name&dir=asc`. Also reads the older `?sort=name:asc` form, so a link
 * saved before column sorting still opens the order it was shared with.
 */
function parseSort(params: URLSearchParams): DocumentsView["sort"] {
  const [rawKey, legacyDir] = (params.get("sort") ?? "").split(":");
  const key = SORT_KEYS.find((k) => k === rawKey);
  if (!key) return DEFAULT_SORT;
  const rawDir = params.get("dir") ?? legacyDir;
  return { key, dir: rawDir === "asc" ? "asc" : "desc" };
}
